
import React, { useState, useEffect } from 'react'; // Added useEffect
import { 
  CloudArrowUpIcon, 
  DocumentTextIcon, 
  ClockIcon,  
  ExclamationTriangleIcon,
  ChevronDownIcon // Added for the select styling
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi'; 
import Toast from '../../components/ui/Toast';

const AssignmentManager = () => {
  const [assignmentsList, setAssignmentsList] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [competitionsList, setCompetitionsList] = useState([]); // State for the dropdown
  const [formData, setFormData] = useState({
    title: '', // This will now store the SELECTED competition title
    assignmentTitle: '',
    description: '',
    timeLimit: 24
  });


  const fetchAssignments = async () => {
  try {
    const data = await adminApi.getAssignments();
    setAssignmentsList(data);
  } catch {
    setNotification({ msg: "Failed to load assignments", type: "error" });
  }
};

  // --- 1. FETCH COMPETITIONS ON MOUNT ---

  useEffect(() => {
  const fetchPrograms = async () => {
    try {
      const res = await adminApi.getCompetitions();
      const data = res.competitions || res.data || [];

      const onlyCompetitions = data.filter(item =>
        item.category?.toLowerCase() === 'competition' ||
        item.type?.toLowerCase() === 'competition' ||
        (!item.category && !item.type)
      );

      setCompetitionsList(onlyCompetitions);
    } catch {
      setNotification({ msg: "Failed to load programs", type: 'error' });
    }
  };

  fetchPrograms();
  fetchAssignments(); // 🔥 NEW

}, []);

  // --- HANDLER: DEPLOY ---
  const handleDeploy = async (e) => {
    e.preventDefault();
    if (!formData.title) return setNotification({ msg: "Please select a competition first!", type: 'error' });

    setLoading(true);
    try {
      await adminApi.deployAssignment({
        title: formData.title.trim(),
        assignmentTitle: formData.assignmentTitle.trim(),
        description: formData.description,
        timeLimit: parseInt(formData.timeLimit)
      });
      setNotification({ msg: "Stage 2 is now LIVE!", type: 'success' });

      await fetchAssignments();
    } catch (err) {
      console.error("Deploy Error Response:", err.response?.data);
      setNotification({ msg: err.response?.data?.message || "Deployment Failed", type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLER: DELETE (RETRACT) ---

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-20 font-mono">
      {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}
      
      <div className="bg-gray-800/50 p-8 rounded-3xl border border-gray-700 shadow-2xl">
        <div className="flex items-center gap-3 mb-8 border-b border-gray-700 pb-6">
          <DocumentTextIcon className="h-8 w-8 text-yellow-500" />
          <div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-tight text-yellow-500">Stage 2: Assignment Master</h1>
            <p className="text-gray-400 text-sm italic">Precision Deployment System</p>
          </div>
        </div>

        <form onSubmit={handleDeploy} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* --- REPLACED INPUT WITH SELECT --- */}
            <div className="relative">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Target Competition</label>
              <div className="relative mt-1">
                <select 
                  required
                  className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-yellow-500 outline-none appearance-none cursor-pointer text-sm"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                >
                  <option value="">-- SELECT COMPETITION --</option>
                  {competitionsList.map((comp) => (
                    <option key={comp.id} value={comp.title}>
                      {comp.title.toUpperCase()}
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="h-4 w-4 text-gray-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Deadline (Hours)</label>
              <div className="flex items-center gap-2 mt-1 bg-gray-900 border border-gray-700 rounded-xl px-4 focus-within:border-yellow-500 transition-all">
                <ClockIcon className="h-5 w-5 text-yellow-500" />
                <input 
                  type="number" required
                  className="w-full bg-transparent py-3 text-white outline-none text-sm"
                  value={formData.timeLimit}
                  onChange={(e) => setFormData({...formData, timeLimit: e.target.value})}
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">Main Heading</label>
            <input 
              type="text" required
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white mt-1 focus:border-yellow-500 outline-none"
              placeholder="e.g. Phase 2: Building the Dashboard Backend"
              value={formData.assignmentTitle}
              onChange={(e) => setFormData({...formData, assignmentTitle: e.target.value})}
            />
          </div>

          <div>
            <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest ml-1">The Brief (Tasks & Rules)</label>
            <textarea 
              required rows="10"
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white mt-1 focus:border-yellow-500 outline-none text-sm leading-relaxed"
              placeholder="Detail the technical requirements here..."
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            ></textarea>
          </div>

          <button 
            type="submit" disabled={loading}
            className={`w-full font-black py-4 rounded-2xl flex items-center justify-center gap-2 transition-all shadow-xl uppercase tracking-widest text-sm
              ${loading ? 'bg-gray-700 text-gray-500' : 'bg-yellow-600 hover:bg-yellow-500 text-white shadow-yellow-600/20 hover:scale-[1.01]'}
            `}
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            {loading ? "SYNCING..." : "RELEASE ASSIGNMENT"}
          </button>
        </form>
      </div>

      <div className="bg-red-900/10 border border-red-900/30 p-6 rounded-3xl flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-red-900/20 rounded-2xl border border-red-900/30">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm uppercase tracking-tight">Retract Assignment</h3>
            <p className="text-red-500/60 text-[10px] uppercase font-black tracking-tighter">Selected Competition: {formData.title || 'None'}</p>
          </div>
        </div>

        {/* DEPLOYED ASSIGNMENTS LIST */}
<div className="bg-gray-800/40 p-6 rounded-3xl border border-gray-700 mt-8">
  <h2 className="text-lg font-bold text-yellow-400 mb-4">
    Deployed Assignments
  </h2>

  {assignmentsList.length === 0 ? (
    <p className="text-gray-500 text-sm italic">
      No assignments deployed yet.
    </p>
  ) : (
    <div className="space-y-4 max-h-[400px] overflow-y-auto">
      {assignmentsList.map((item) => (
        <div
          key={item.id}
          className="bg-gray-900 border border-gray-700 rounded-xl p-4"
        >
          {/* HEADER */}
          <div className="flex justify-between items-center">
            <div>
              <p className="text-white font-bold text-sm">
                {item.competition_title}
              </p>
              <p className="text-gray-500 text-xs">
                {item.title}
              </p>
            </div>

            <button
              onClick={() =>
                setExpandedId(expandedId === item.id ? null : item.id)
              }
              className="text-yellow-400 text-xs font-bold"
            >
              {expandedId === item.id ? "CLOSE" : "VIEW"}
            </button>
          </div>

          {/* EXPANDED CONTENT */}
          {expandedId === item.id && (
            <div className="mt-4 border-t border-gray-700 pt-4 space-y-3">
              <p className="text-gray-300 text-sm whitespace-pre-line">
                {item.description}
              </p>

              <p className="text-gray-500 text-xs">
                Time Limit: {item.time_limit_hours} hours
              </p>

              <button
                onClick={async () => {
                  const confirmDelete = window.confirm(
                    "Are you sure you want to permanently delete this assignment?"
                  );

                  if (!confirmDelete) return;

                  try {
                    await adminApi.deleteAssignment(item.competition_id);
                    setNotification({
                      msg: "Assignment deleted successfully!",
                      type: "success",
                    });
                    setExpandedId(null);
                    fetchAssignments();
                  } catch {
                    setNotification({
                      msg: "Deletion failed",
                      type: "error",
                    });
                  }
                }}
                className="bg-red-600/10 hover:bg-red-600 border border-red-600/30 text-red-500 hover:text-white px-4 py-2 rounded-lg text-xs font-bold transition-all"
              >
                DELETE ASSIGNMENT
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  )}
</div>
      </div>
    </div>
  );
};

export default AssignmentManager;